import { computed } from 'vue';
import { groupPackageRows } from '@/helpers/service-package-rows';
import { useServicesStore } from '@/stores/services';
import unleash from '@/unleash';

type SummaryLine = {
  servicePackageServiceId?: number | null;
  name?: string | null;
  price?: number | null;
  originalPrice?: number | null;
  quantity?: number | null;
};

export type OrderSummaryRow<Line> =
  | { kind: 'item'; key: string; item: Line }
  | {
      kind: 'package';
      key: string;
      name: string;
      serviceNames: string[];
      quantity: number;
      price: number;
      originalPrice: number;
      discountPercentage: number;
    };

export const useOrderSummaryRows = <Line extends SummaryLine>(
  lines: () => Line[],
) => {
  const { servicePackageByServicePackageServiceId } = useServicesStore();
  const servicePackagesEnabled = unleash.isEnabled('ServicePackages');

  return computed<OrderSummaryRow<Line>[]>(() => {
    const items = lines();

    return groupPackageRows(items, (line) =>
      servicePackagesEnabled
        ? servicePackageByServicePackageServiceId(line.servicePackageServiceId)
        : undefined,
    ).map((row) => {
      if (row.kind !== 'package') {
        return { kind: 'item', key: row.key, item: items[row.indexes[0]] };
      }

      const packageLines = row.indexes.map((index) => items[index]);
      const total = (amount: (line: Line) => number | null | undefined) =>
        packageLines.reduce(
          (sum, line) => sum + (amount(line) ?? 0) * (line.quantity ?? 1),
          0,
        );
      const price = total((line) => line.price);
      const originalPrice = total((line) => line.originalPrice ?? line.price);
      const discount = Math.abs(originalPrice) - Math.abs(price);

      return {
        kind: 'package',
        key: row.key,
        name: row.servicePackage.name,
        serviceNames: packageLines.map((line) => line.name ?? ''),
        quantity: packageLines[0].quantity ?? 1,
        price,
        originalPrice,
        discountPercentage:
          discount > 0
            ? Math.round((discount / Math.abs(originalPrice)) * 100)
            : 0,
      };
    });
  });
};
